"use client";

import type { CashflowSummary } from "@/types";
import { TransactionDateRangePicker } from "@/components/transactions/TransactionDateRangePicker";
import {
  PROJECTION_SCENARIO_SELECT_OPTIONS,
  type ProjectionScenario,
} from "@/constants/projectionScenarioSelect";
import { useCashflowContext } from "./CashflowContext";

type Props = {
  /** When set (e.g. consolidated view), export uses this snapshot instead of solo cashflow context. */
  exportSummary?: CashflowSummary;
};

export function CashflowProjectionsPageHeader({ exportSummary }: Props) {
  const {
    dateFrom,
    dateTo,
    setDateFrom,
    setDateTo,
    projectionScenario,
    setProjectionScenario,
  } = useCashflowContext();

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4 md:p-[17px]">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="min-w-0 space-y-1">
          <h1 className="text-2xl font-semibold leading-8 text-[#0f172b]">
            Cashflow Projections Table
          </h1>
          <p className="text-sm leading-5 text-slate-500">
            Edit projected values for future months
          </p>
        </div>

        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-4 gap-y-3 lg:justify-end">
          <div className="flex flex-wrap items-center gap-2">
            <TransactionDateRangePicker
              dateFrom={dateFrom || undefined}
              dateTo={dateTo || undefined}
              commitDateRange={(from, to) => {
                setDateFrom(from ?? "");
                setDateTo(to ?? "");
              }}
              rangeHint="Show cashflow between"
              ariaRangeKind="Cashflow date range"
            />
          </div>

          <select
            value={projectionScenario}
            onChange={(e) =>
              setProjectionScenario(e.target.value as ProjectionScenario)
            }
            className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-2 text-sm font-medium text-slate-700 outline-none ring-emerald-500 focus:ring-2"
            aria-label="Projection scenario"
          >
            {PROJECTION_SCENARIO_SELECT_OPTIONS.map((tab) => (
              <option key={tab.value} value={tab.value}>
                {tab.label}
              </option>
            ))}
          </select>

          <ExportToolbarButton exportSummary={exportSummary} />
        </div>
      </div>
    </section>
  );
}

function ExportToolbarButton({
  exportSummary,
}: {
  exportSummary?: CashflowSummary;
}) {
  const { summary, currency } = useCashflowContext();
  const effective = exportSummary ?? summary;

  function handleExport() {
    if (!effective || effective.months.length === 0) return;
    import("./cashflowProjectionExport").then(
      ({ downloadCashflowProjectionCsv }) => {
        void downloadCashflowProjectionCsv(effective, currency);
      },
    );
  }

  return (
    <button
      type="button"
      onClick={handleExport}
      disabled={!effective || effective.months.length === 0}
      className="inline-flex h-9 items-center gap-2 rounded-lg border border-black/10 bg-white px-3 py-1.5 text-sm font-medium text-[#0f172a] shadow-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
    >
      <svg
        className="size-4 shrink-0 text-slate-600"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        aria-hidden
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
        />
      </svg>
      Export
    </button>
  );
}
